import * as THREE from 'three'
import {UniformsUpdater} from './utils/UniformsUpdater'
import {ADVISOR_CONSTANTS} from '@/components/MapContext/ThreeJs/DataOfThreeJS/AdvisorObject/AdvisorConstants'
import {advisorFragmentShader} from './DataOfThreeJS/AdvisorObject/AdvisorShaders'
import {advisorVertexShader} from '@/components/MapContext/ThreeJs/DataOfThreeJS/AdvisorObject/AdvisorShaders'

export class AdvisorObject extends THREE.Group {
  private dome!: THREE.Mesh
  private shaderMaterial?: THREE.ShaderMaterial
  private simpleMaterial?: THREE.MeshStandardMaterial
  private readonly radius: number
  private readonly color: number
  private readonly useShader: boolean

  constructor(
    radius: number = ADVISOR_CONSTANTS.DEFAULT_RADIUS,
    color: number = ADVISOR_CONSTANTS.DEFAULT_COLOR,
    useShader: boolean = true,
    resolution?: THREE.Vector2
  ) {
    super()

    if (radius <= 0) {
      throw new Error('AdvisorObject: radius must be positive')
    }
    if (color < 0 || color > 0xffffff) {
      throw new Error(
        'AdvisorObject: color must be a valid hex color (0x000000 - 0xffffff)'
      )
    }

    this.radius = radius
    this.color = color
    this.useShader = useShader

    const geometry = this.createGeometry(radius)
    const material = useShader
      ? this.createShaderMaterial(radius, color, resolution)
      : this.createSimpleMaterial(color)
    this.dome = this.createDome(geometry, material)

    this.add(this.dome)
    this.userData.isDome = true
  }

  /**
   * Создание геометрии купола
   */
  private createGeometry(radius: number): THREE.SphereGeometry {
    return new THREE.SphereGeometry(
      radius,
      ADVISOR_CONSTANTS.GEOMETRY_SEGMENTS_WIDTH,
      ADVISOR_CONSTANTS.GEOMETRY_SEGMENTS_HEIGHT,
      ADVISOR_CONSTANTS.GEOMETRY_PHI_START,
      ADVISOR_CONSTANTS.GEOMETRY_PHI_LENGTH,
      ADVISOR_CONSTANTS.GEOMETRY_THETA_START,
      ADVISOR_CONSTANTS.GEOMETRY_THETA_LENGTH
    )
  }

  /**
   * Создание шейдерного материала
   */
  private createShaderMaterial(
    radius: number,
    color: number,
    resolution?: THREE.Vector2
  ): THREE.ShaderMaterial {
    const noiseTexture = this.createNoiseTexture()
    const reflectionTexture = this.createReflectionTexture()

    const res =
      resolution || new THREE.Vector2(window.innerWidth, window.innerHeight)

    this.shaderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: {value: 0},
        resolution: {value: res},
        radius: {value: radius},
        baseColor: {value: new THREE.Color(color)},
        noiseTexture: {value: noiseTexture},
        reflectionTexture: {value: reflectionTexture},
      },
      vertexShader: advisorVertexShader,
      fragmentShader: advisorFragmentShader,
      transparent: true,
      side: THREE.DoubleSide,
    })

    return this.shaderMaterial
  }

  /**
   * Создание простого материала (без шейдера)
   */
  private createSimpleMaterial(color: number): THREE.MeshStandardMaterial {
    this.simpleMaterial = new THREE.MeshStandardMaterial({
      color: color,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
    })
    return this.simpleMaterial
  }

  /**
   * Создание купола (mesh)
   */
  private createDome(
    geometry: THREE.SphereGeometry,
    material: THREE.ShaderMaterial | THREE.MeshStandardMaterial
  ): THREE.Mesh {
    return new THREE.Mesh(geometry, material)
  }

  /**
   * Создание текстуры шума
   */
  private createNoiseTexture(): THREE.DataTexture {
    const size = ADVISOR_CONSTANTS.TEXTURE_SIZE
    const data = new Uint8Array(size * size * 4)

    for (let i = 0; i < size * size; i++) {
      const value = Math.random() * 255
      const idx = i * 4
      data[idx] = value
      data[idx + 1] = value
      data[idx + 2] = value
      data[idx + 3] = 255
    }

    const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat)
    texture.needsUpdate = true
    return texture
  }

  /**
   * Создание текстуры отражения
   */
  private createReflectionTexture(): THREE.DataTexture {
    const size = ADVISOR_CONSTANTS.TEXTURE_SIZE
    const data = new Uint8Array(size * size * 4)

    for (let i = 0; i < size * size; i++) {
      const idx = i * 4
      data[idx] = ADVISOR_CONSTANTS.REFLECTION_TEXTURE_R
      data[idx + 1] = ADVISOR_CONSTANTS.REFLECTION_TEXTURE_G
      data[idx + 2] = ADVISOR_CONSTANTS.REFLECTION_TEXTURE_B
      data[idx + 3] = ADVISOR_CONSTANTS.REFLECTION_TEXTURE_A
    }

    const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat)
    texture.needsUpdate = true
    return texture
  }

  /**
   * Обновление uniforms объекта
   * Использует UniformsUpdater для единообразия с другими объектами
   * @param cameraPos Позиция камеры (не используется, но оставлен для обратной совместимости)
   * @param currentTime Текущее время в секундах (опционально, если не передано - используется внутренний счетчик)
   */
  update(cameraPos: THREE.Vector3, currentTime: number) {
    if (!this.shaderMaterial) {
      return
    }
    UniformsUpdater.updateMaterialUniforms(this.shaderMaterial, currentTime, [
      'time',
    ])
  }

  dispose() {
    this.dome.geometry.dispose()
    if (this.shaderMaterial) {
      if (this.shaderMaterial.uniforms.noiseTexture?.value) {
        this.shaderMaterial.uniforms.noiseTexture.value.dispose()
      }
      if (this.shaderMaterial.uniforms.reflectionTexture?.value) {
        this.shaderMaterial.uniforms.reflectionTexture.value.dispose()
      }
      this.shaderMaterial.dispose()
    }
    if (this.simpleMaterial) {
      this.simpleMaterial.dispose()
    }
  }
}
